import {CabinetMesh} from 'components/customer/RoomPlanner/types';
import {ComputedDimension} from 'components/customer/RoomPlanner/lib/useRoomPlanner';
import * as THREE from 'three';
import {OBB} from 'three/examples/jsm/math/OBB.js';
import Camera from 'components/customer/RoomPlanner/lib/use3DRPCameraSetup';
import {
    DUPLICATE_PLACEMENT_GAP,
    DUPLICATE_FLOOR_ROW_Z_OFFSET,
    DUPLICATE_FLOOR_WALL_MARGIN,
    DUPLICATE_FLOOR_BOUNDS_PADDING,
    getDuplicateFloorPlacementBounds,
    placeNewUpperCabinetOnWall,
} from 'components/customer/RoomPlanner/helpers/cabinetPlacement';
import {isHangingLayoutItem as itemHangsOnWall} from 'components/customer/RoomPlanner/helpers/placeholder/style';

/** Default mm thickness used when product values omit ext/carc thickness. */
const BULKHEAD_THICKNESS_FALLBACK_MM = 16.5;

// Local copy of getDirection — importing from cabinetPosition pulls in a heavy
// Product/WYSIWYG dependency graph that breaks Node-based unit tests (no window).
const getFacingFromRotation = (
    rotation: number
): 'normal' | 'right' | 'back' | 'left' => {
    const normalizedRotation =
        ((rotation % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);

    if (Math.abs(normalizedRotation) < 0.1) return 'normal';
    if (Math.abs(normalizedRotation - Math.PI / 2) < 0.1) return 'right';
    if (Math.abs(normalizedRotation - Math.PI) < 0.1) return 'back';
    if (Math.abs(normalizedRotation - (3 * Math.PI) / 2) < 0.1) return 'left';
    return 'normal';
};

/**
 * World-unit offset that clears a bulkhead from the wall by ext + carc thickness.
 * @param {CabinetMesh} cabinet - Bulkhead cabinet (reads thickness from userData.values)
 * @param {number} [scale=0.2] - mm → scene scale
 * @return {number} Combined thickness in world units
 */
export const getBulkheadSpawnOffset = (
    cabinet: CabinetMesh,
    scale = 0.2
): number => {
    const values = cabinet.userData.values as
        | {cabinet_ext_thickness?: number; cabinet_carc_thickness?: number}
        | undefined;
    const extThickness =
        Number(values?.cabinet_ext_thickness) || BULKHEAD_THICKNESS_FALLBACK_MM;
    const carcThickness =
        Number(values?.cabinet_carc_thickness) ||
        BULKHEAD_THICKNESS_FALLBACK_MM;
    return (extThickness + carcThickness) * scale;
};

/**
 * Nudges a bulkhead's spawn position so it clears the wall it faces.
 * Front/back → offset along −Z; left/right → offset along +X.
 * @param {THREE.Vector3} spawnPosition - Position to adjust (mutated and returned)
 * @param {CabinetMesh} cabinet - Bulkhead whose facing and thicknesses drive the offset
 * @param {number} [scale=0.2] - mm → scene scale
 * @return {THREE.Vector3} The same spawnPosition, after the offset is applied
 */
export const applyBulkheadSpawnOffset = (
    spawnPosition: THREE.Vector3,
    cabinet: CabinetMesh,
    scale = 0.2
): THREE.Vector3 => {
    const spawnOffset = getBulkheadSpawnOffset(cabinet, scale);
    if (spawnOffset === 0) return spawnPosition;

    const userDirection = cabinet.userData.currentDirection;
    let facing: 'normal' | 'right' | 'back' | 'left' = getFacingFromRotation(
        cabinet.rotation.z
    );
    if (
        userDirection === 'left' ||
        userDirection === 'right' ||
        userDirection === 'back'
    ) {
        facing = userDirection;
    } else if (userDirection === 'front') {
        facing = 'normal';
    }

    if (facing === 'left' || facing === 'right') {
        spawnPosition.x += spawnOffset;
    } else if (facing === 'back') {
        // Front wall — add along +Z (subtract leaves a gap)
        spawnPosition.z += spawnOffset;
    } else {
        // Back wall (normal/front) — subtract along −Z
        spawnPosition.z -= spawnOffset;
    }

    return spawnPosition;
};

// Utility to check OBB intersection between two meshes
const checkOBBIntersection = (
    mesh1: THREE.Object3D,
    mesh2: THREE.Object3D
): boolean => {
    // Compute OBB for mesh1
    const box1 = new THREE.Box3().setFromObject(mesh1);
    const obb1 = new OBB();
    obb1.center.copy(box1.getCenter(new THREE.Vector3()));
    obb1.halfSize.copy(box1.getSize(new THREE.Vector3()).multiplyScalar(0.5));
    // Extract rotation as Matrix3
    const rot1 = new THREE.Matrix3().setFromMatrix4(mesh1.matrixWorld);
    obb1.rotation.copy(rot1);

    // Compute OBB for mesh2
    const box2 = new THREE.Box3().setFromObject(mesh2);
    const obb2 = new OBB();
    obb2.center.copy(box2.getCenter(new THREE.Vector3()));
    obb2.halfSize.copy(box2.getSize(new THREE.Vector3()).multiplyScalar(0.5));
    // Extract rotation as Matrix3
    const rot2 = new THREE.Matrix3().setFromMatrix4(mesh2.matrixWorld);
    obb2.rotation.copy(rot2);

    // Check for intersection
    return obb1.intersectsOBB(obb2);
};

const getRightmostBBoxMaxX = (
    cabinets: THREE.Group<THREE.Object3DEventMap>[]
): number => {
    let maxX = -Infinity;
    for (const c of cabinets) {
        const box = new THREE.Box3().setFromObject(c);
        if (box.max.x > maxX) maxX = box.max.x;
    }
    return maxX;
};

// Returns true when a cabinet's world bounding box overlaps the Z band
// [rowZ − tolerance, rowZ + tolerance].  Using the bbox rather than
// position.z is essential for L-shaped cabinets whose pivot sits at the
// inner corner of the L instead of at the geometric back face.
const occupiesZBand = (
    c: THREE.Group<THREE.Object3DEventMap>,
    rowZ: number,
    tolerance: number
): boolean => {
    const box = new THREE.Box3().setFromObject(c);
    return box.min.z <= rowZ + tolerance && box.max.z >= rowZ - tolerance;
};

/**
 * Wall cabinets and upper placeholders pack independently of floor
 * cabinets / floor appliances so one run does not push the other along X.
 * @param {THREE.Group} item Cabinet or placeholder
 * @return {boolean}
 */
const isHangingLayoutItem = (
    item: THREE.Group<THREE.Object3DEventMap>
): boolean => itemHangsOnWall(item as CabinetMesh);

const isSameLayoutItem = (
    a: THREE.Group<THREE.Object3DEventMap>,
    b: THREE.Group<THREE.Object3DEventMap>
): boolean => {
    if (a === b || a.uuid === b.uuid) return true;
    return (
        a.userData?.id === b.userData?.id &&
        Boolean(a.userData?.isPlaceholder) ===
            Boolean(b.userData?.isPlaceholder)
    );
};

const boxesOverlap3D = (boxA: THREE.Box3, boxB: THREE.Box3): boolean =>
    boxA.min.x < boxB.max.x - DUPLICATE_PLACEMENT_GAP &&
    boxA.max.x > boxB.min.x + DUPLICATE_PLACEMENT_GAP &&
    boxA.min.y < boxB.max.y - DUPLICATE_PLACEMENT_GAP &&
    boxA.max.y > boxB.min.y + DUPLICATE_PLACEMENT_GAP &&
    boxA.min.z < boxB.max.z - DUPLICATE_PLACEMENT_GAP &&
    boxA.max.z > boxB.min.z + DUPLICATE_PLACEMENT_GAP;

/**
 * Slide a newly placed cabinet right until its 3D bounds no longer intersect
 * other cabinets or placeholders. Used after default spawn and add-beside.
 * @param {CabinetMesh} cabinetGroup Cabinet that was just positioned
 * @param {THREE.Group[]} existingItems Cabinets and placeholders already in the room
 * @return {void}
 */
export const nudgeCabinetClearOfOverlaps = (
    cabinetGroup: CabinetMesh,
    existingItems: THREE.Group<THREE.Object3DEventMap>[]
): void => {
    for (let i = 0; i < 20; i++) {
        cabinetGroup.updateMatrixWorld(true);
        const ownBox = new THREE.Box3().setFromObject(cabinetGroup);
        let rightmost = -Infinity;
        let hasOverlap = false;

        for (const other of existingItems) {
            if (
                other.userData?.isTempMesh ||
                isSameLayoutItem(other, cabinetGroup)
            ) {
                continue;
            }
            other.updateMatrixWorld(true);
            const otherBox = new THREE.Box3().setFromObject(other);
            if (!boxesOverlap3D(ownBox, otherBox)) continue;
            hasOverlap = true;
            if (otherBox.max.x > rightmost) rightmost = otherBox.max.x;
        }

        if (!hasOverlap) return;

        cabinetGroup.position.x +=
            rightmost + DUPLICATE_PLACEMENT_GAP - ownBox.min.x;
    }
};

// Push a floor (non-upper) cabinet right past any upper cabinets that truly
// occupy the same space.  Child meshes are not loaded yet at spawn time, so the
// new cabinet's extents come from userData.values / future-bbox samples rather
// than setFromObject.  Existing uppers are already in the scene, so their
// bboxes are reliable.
//
// Overlap requires X + Y + Z:
//   • Y distinguishes tall (reaches upper zone) from base (stays below)
//   • Z keeps row-2+ cabinets from being pushed by back-wall uppers
// If a push would exceed the row's max X, advance to the next row instead of
// placing the cabinet outside the room.
const pushPastOverlappingUpperCabinets = (
    cabinetGroup: CabinetMesh,
    existingCabinets: THREE.Group<THREE.Object3DEventMap>[],
    scale: number,
    roomBounds: {minX: number; maxX: number},
    getEffectiveMaxX: (rowZ: number) => number,
    rowZOffset: number,
    rowTolerance: number,
    zPivotOffset: number,
    futureZDepth: number,
    sameTypeCabinets: THREE.Group<THREE.Object3DEventMap>[]
): void => {
    if (cabinetGroup.userData.isUpperProduct) return;

    const upperCabinets = existingCabinets.filter(
        (c) => !c.userData.isTempMesh && isHangingLayoutItem(c)
    );
    if (upperCabinets.length === 0) return;

    const values =
        (cabinetGroup.userData.values as unknown as Record<string, unknown>) ??
        {};
    const height = Number(values.cabinet_height ?? 0) * scale;
    const width = Number(values.cabinet_width ?? 0) * scale;
    if (height <= 0 || width <= 0) return;

    const advanceToNextRow = (): void => {
        const nextZ = cabinetGroup.position.z + rowZOffset;
        const cabinetsInNextRow = sameTypeCabinets.filter(
            (c) =>
                !c.userData.isTempMesh && occupiesZBand(c, nextZ, rowTolerance)
        );

        let newPositionX = roomBounds.minX + 0.5;
        if (cabinetsInNextRow.length > 0) {
            const rowRightmostMaxX = getRightmostBBoxMaxX(cabinetsInNextRow);
            if (rowRightmostMaxX > -Infinity) {
                newPositionX = rowRightmostMaxX + DUPLICATE_PLACEMENT_GAP;
            }
        }

        cabinetGroup.position.set(newPositionX, cabinetGroup.position.y, nextZ);
        cabinetGroup.userData.positionedInAnotherRow = true;
        cabinetGroup.updateMatrixWorld();
    };

    for (let i = 0; i < 20; i++) {
        const newMinX = cabinetGroup.position.x;
        const newMaxX = cabinetGroup.position.x + width;
        const newMinY = cabinetGroup.position.y;
        const newMaxY = cabinetGroup.position.y + height;
        // Future (post-rotation) Z band — matches existing cabinets' world Z.
        const newMinZ = cabinetGroup.position.z + zPivotOffset;
        const newMaxZ =
            newMinZ + (futureZDepth > 0 ? futureZDepth : rowZOffset);

        let rightmostUpperX = -Infinity;
        for (const upper of upperCabinets) {
            const upperBox = new THREE.Box3().setFromObject(upper);
            const xOverlap =
                Math.min(newMaxX, upperBox.max.x) -
                Math.max(newMinX, upperBox.min.x);
            const yOverlap =
                Math.min(newMaxY, upperBox.max.y) -
                Math.max(newMinY, upperBox.min.y);
            const zOverlap =
                Math.min(newMaxZ, upperBox.max.z) -
                Math.max(newMinZ, upperBox.min.z);
            if (xOverlap > 0 && yOverlap > 0 && zOverlap > 0) {
                if (upperBox.max.x > rightmostUpperX)
                    rightmostUpperX = upperBox.max.x;
            }
        }

        if (rightmostUpperX === -Infinity) break;

        const pushedX = rightmostUpperX + DUPLICATE_PLACEMENT_GAP;
        // Would the cabinet sit past the row's right bound? → new row instead.
        if (pushedX + width > getEffectiveMaxX(cabinetGroup.position.z)) {
            advanceToNextRow();
            continue;
        }

        cabinetGroup.position.x = pushedX;
        cabinetGroup.updateMatrixWorld();
    }
};

export const determineSpawnLocation = (
    cabinetGroup: CabinetMesh,
    existingCabinets: THREE.Group<THREE.Object3DEventMap>[],
    initialPosition: THREE.Vector3,
    dimensions: ComputedDimension,
    roomType?: string
): THREE.Vector3 => {
    const {scale, roomWidth} = dimensions;
    cabinetGroup.scale.set(scale, scale, scale);
    cabinetGroup.updateMatrixWorld();

    // ─── Upper-cabinet path: wall-wrapping logic ─────────────────────────────
    // Upper cabinets mount on walls and wrap clockwise around the room when a
    // wall is full — matching the behaviour of duplicateCabinet for uppers.
    // Apply the final -90° X rotation temporarily so wall-clamping geometry
    // (autoRotateClamping inside resolvePositionOnWall) sees the correct bounds,
    // then restore rotation so the caller can apply it unconditionally as usual.
    if (cabinetGroup.userData.isUpperProduct && roomType) {
        const savedRotX = cabinetGroup.rotation.x;
        cabinetGroup.rotation.x = -Math.PI / 2;
        cabinetGroup.updateMatrixWorld();

        cabinetGroup.position.copy(initialPosition);
        cabinetGroup.updateMatrixWorld();

        const upperExisting = existingCabinets.filter(
            (c) => !c.userData.isTempMesh
        ) as unknown as CabinetMesh[];

        const placed = placeNewUpperCabinetOnWall({
            cabinet: cabinetGroup,
            existingCabinets: upperExisting,
            initialPosition,
            scene: Camera.scene,
            roomType,
        });

        if (!placed) {
            // No wall space found — fall back to initial position so the cabinet
            // still appears in the room rather than staying off-screen.
            cabinetGroup.position.copy(initialPosition);
        }

        cabinetGroup.rotation.x = savedRotX;
        cabinetGroup.updateMatrixWorld();

        return cabinetGroup.position.clone();
    }
    // ─────────────────────────────────────────────────────────────────────────

    // ─── Future-bbox sampling ────────────────────────────────────────────────
    // renderCabinetGroup applies rotation.x = -PI/2 unconditionally AFTER this
    // function returns.  All existing cabinets in the scene already carry that
    // rotation, so their bounding boxes are measured in the rotated coordinate
    // space.  Without accounting for the rotation here, the new cabinet's Z
    // extents are computed on the wrong axis (Y and Z are swapped), making
    // ROW_Z_OFFSET wrong and the Z-push comparison meaningless.
    //
    // Fix: temporarily apply the same rotation, sample the bounding box to get
    // the future Z-depth and the offset from position.z to bbox.min.z, then
    // restore rotation so the caller can apply it on its own terms.
    const savedRotX = cabinetGroup.rotation.x;
    cabinetGroup.rotation.x = -Math.PI / 2;
    cabinetGroup.updateMatrixWorld();
    const futureSampleBbox = new THREE.Box3().setFromObject(cabinetGroup);
    const futureZDepth = futureSampleBbox.max.z - futureSampleBbox.min.z;
    // zPivotOffset = distance from position.z to bbox.min.z in rotated space.
    // Constant for a given model/scale — position-independent.
    const zPivotOffset = futureSampleBbox.min.z - cabinetGroup.position.z;
    cabinetGroup.rotation.x = savedRotX;
    cabinetGroup.updateMatrixWorld();

    const isNonRectShapePanel = Boolean(
        cabinetGroup.userData.isKeyShapePanel ||
        cabinetGroup.userData.isUShapedPanel ||
        cabinetGroup.userData.isLShapePanel
    );

    // Row-to-row Z step: actual world Z-depth after rotation + a tiny gap.
    // Falls back to the constant used by duplicateFloorCabinet when the model
    // has no geometry loaded yet (futureZDepth = 0).
    const ROW_Z_OFFSET = isNonRectShapePanel
        ? DUPLICATE_FLOOR_ROW_Z_OFFSET + DUPLICATE_PLACEMENT_GAP
        : futureZDepth > 0
          ? futureZDepth + DUPLICATE_PLACEMENT_GAP
          : DUPLICATE_FLOOR_ROW_Z_OFFSET + DUPLICATE_PLACEMENT_GAP;

    // Same-row grouping tolerance: half the row step keeps rows from bleeding
    // into each other even with minor Z jitter between cabinet types.
    const ROW_TOLERANCE = ROW_Z_OFFSET * 0.5;
    // ────────────────────────────────────────────────────────────────────────

    // Derive room X bounds the same way duplicateFloorCabinet does: prefer
    // actual floor/wall mesh bounds, fall back to nominal roomWidth.
    const placementBounds = getDuplicateFloorPlacementBounds(Camera.scene);
    const pad = DUPLICATE_FLOOR_BOUNDS_PADDING;
    const roomBounds = {
        minX: placementBounds
            ? placementBounds.min.x + pad
            : -roomWidth / 2 + DUPLICATE_FLOOR_WALL_MARGIN,
        maxX: placementBounds
            ? placementBounds.max.x - pad
            : roomWidth / 2 - DUPLICATE_FLOOR_WALL_MARGIN,
    };

    let lShapedNotchX = Infinity;
    let lShapedNotchZ = -Infinity;
    if (roomType === 'LSHAPED') {
        const readWallGeomBox = (wallName: string): THREE.Box3 | null => {
            const wall = Camera.scene.getObjectByName(
                wallName
            ) as THREE.Mesh | null;
            if (!wall?.geometry) return null;
            if (!wall.geometry.boundingBox) wall.geometry.computeBoundingBox();
            const local = wall.geometry.boundingBox;
            if (!local) return null;
            return local.clone().applyMatrix4(wall.matrixWorld);
        };
        const leftWallBox = readWallGeomBox('leftWidth2Wall');
        const rightWallBox = readWallGeomBox('rightWidth2Wall');
        if (leftWallBox) lShapedNotchX = leftWallBox.min.x;
        if (rightWallBox) lShapedNotchZ = rightWallBox.min.z;
    }

    const getEffectiveMaxX = (rowZ: number): number => {
        if (
            lShapedNotchX < Infinity &&
            lShapedNotchZ > -Infinity &&
            rowZ > lShapedNotchZ - ROW_TOLERANCE
        ) {
            return Math.min(roomBounds.maxX, lShapedNotchX);
        }
        return roomBounds.maxX;
    };
    // ─────────────────────────────────────────────────────────────────────────

    // Initial X spawn: after the rightmost cabinet in the first (back-wall) row
    let spawnX = initialPosition.x + cabinetGroup.userData.widthOffset - 0.1;

    const isPanelLike =
        !cabinetGroup.userData.isManualPanel &&
        (cabinetGroup.userData.isPanelProduct ||
            (cabinetGroup.userData.isFillerProduct &&
                !cabinetGroup.userData.isVerminProduct));

    if (isPanelLike) {
        const currentBox = new THREE.Box3().setFromObject(cabinetGroup);
        spawnX += currentBox.max.x - currentBox.min.x;
    }

    // Only consider cabinets of the same type (base vs upper) for all
    // placement decisions so that base and upper cabinets have independent
    // spawn sequences and don't push each other along the X axis.
    // Floor cabinets pack with floor placeholders; wall cabinets pack with
    // rangehoods. Mixing the two would shove a base run past a hanging hood.
    const newItemIsHanging = isHangingLayoutItem(cabinetGroup);
    const sameTypeCabinets = existingCabinets.filter(
        (c) => isHangingLayoutItem(c) === newItemIsHanging
    );

    if (sameTypeCabinets.length > 0) {
        // Only treat a cabinet as a "first-row" obstacle when it physically
        // occupies the back-wall Z band.  The bbox overlap check (occupiesZBand)
        // is used instead of position.z so that L-shaped cabinets — whose pivot
        // sits at the inner corner of the L, not at the back face — are
        // correctly included.  The guard also prevents cabinets that were
        // manually dragged to the middle of the room from being mistaken for
        // back-wall cabinets (their bbox won't overlap initialPosition.z ± tolerance).
        const firstRowCabinets = sameTypeCabinets.filter(
            (c) =>
                !c.userData.isTempMesh &&
                !c.userData.positionedInAnotherRow &&
                occupiesZBand(c, initialPosition.z, ROW_TOLERANCE)
        );
        if (firstRowCabinets.length > 0) {
            const rightmostMaxX = getRightmostBBoxMaxX(firstRowCabinets);
            if (rightmostMaxX > -Infinity) {
                spawnX = rightmostMaxX + DUPLICATE_PLACEMENT_GAP;
                if (isPanelLike) {
                    const currentBox = new THREE.Box3().setFromObject(
                        cabinetGroup
                    );
                    spawnX += currentBox.max.x - currentBox.min.x;
                }
            }
        }
    }

    const spawnPosition = new THREE.Vector3(
        spawnX,
        initialPosition.y,
        initialPosition.z
    );

    // When no same-type cabinets exist, ignore initialPosition.x — it may be
    // shifted by accumulated product widths (other types) or by computedWidth's
    // fixed margin, which can land a full panel-width into the room ("2nd
    // column"). Always anchor the first same-type cabinet to the room's left
    // bound, matching the new-row logic.
    if (sameTypeCabinets.length === 0) {
        const correctX = roomBounds.minX + 0.5;
        cabinetGroup.position.set(
            correctX,
            initialPosition.y,
            initialPosition.z
        );
        cabinetGroup.updateMatrixWorld();
        pushPastOverlappingUpperCabinets(
            cabinetGroup,
            existingCabinets,
            scale,
            roomBounds,
            getEffectiveMaxX,
            ROW_Z_OFFSET,
            ROW_TOLERANCE,
            zPivotOffset,
            futureZDepth,
            sameTypeCabinets
        );
        return cabinetGroup.position.clone();
    }

    cabinetGroup.position.copy(spawnPosition);
    cabinetGroup.updateMatrixWorld();

    // currentRowZ tracks which row the cabinet is currently in and advances by
    // ROW_Z_OFFSET every time the X boundary is exceeded.
    let currentRowZ = initialPosition.z;
    let hasOverlap = true;
    const maxIterations = 20;
    let iterations = 0;

    while (hasOverlap && iterations < maxIterations) {
        hasOverlap = false;
        iterations++;

        const currentNewProductBox = new THREE.Box3().setFromObject(
            cabinetGroup
        );

        if (currentNewProductBox.max.x > getEffectiveMaxX(currentRowZ)) {
            // ── Advance to next row ──────────────────────────────────────────
            currentRowZ += ROW_Z_OFFSET;

            // Find the rightmost same-type cabinet already in the target row
            const cabinetsInNextRow = sameTypeCabinets.filter(
                (c) =>
                    !c.userData.isTempMesh &&
                    occupiesZBand(c, currentRowZ, ROW_TOLERANCE)
            );

            let newPositionX = roomBounds.minX + 0.5;
            if (cabinetsInNextRow.length > 0) {
                const rowRightmostMaxX =
                    getRightmostBBoxMaxX(cabinetsInNextRow);
                if (rowRightmostMaxX > -Infinity) {
                    newPositionX = rowRightmostMaxX + DUPLICATE_PLACEMENT_GAP;
                    if (
                        cabinetGroup.userData.isPanelProduct ||
                        (cabinetGroup.userData.isFillerProduct &&
                            !cabinetGroup.userData.isVerminProduct)
                    ) {
                        const cb = new THREE.Box3().setFromObject(cabinetGroup);
                        newPositionX += cb.max.x - cb.min.x;
                    }
                }
            }

            cabinetGroup.position.set(
                newPositionX,
                cabinetGroup.position.y,
                currentRowZ
            );
            cabinetGroup.userData.positionedInAnotherRow = true;
            cabinetGroup.updateMatrixWorld();

            // ── Z-collision guard ────────────────────────────────────────────
            // Use the future bbox (post-rotation) min.z for the new cabinet,
            // and compare against the actual world bbox.max.z of every
            // same-type cabinet in a previous row.  Both are in the same
            // rotated space.
            const futureBboxMinZ = cabinetGroup.position.z + zPivotOffset;
            let prevRowsMaxBboxZ = -Infinity;
            for (const c of sameTypeCabinets) {
                if (c.userData.isTempMesh) continue;
                // L-shaped cabinets span two walls; their Z extent mixes the
                // back-wall depth with the side-wall run, making this 1-D guard
                // unreliable.  Their X-Z overlap is handled by the OBB check
                // in the same-row loop, so exclude them here.
                if (c.userData.isLShapedProduct) continue;
                // Only consider cabinets clearly behind our new row
                if (c.position.z >= currentRowZ - ROW_TOLERANCE) continue;
                const cBox = new THREE.Box3().setFromObject(c);
                if (cBox.max.z > prevRowsMaxBboxZ)
                    prevRowsMaxBboxZ = cBox.max.z;
            }
            if (
                prevRowsMaxBboxZ > -Infinity &&
                futureBboxMinZ < prevRowsMaxBboxZ + DUPLICATE_PLACEMENT_GAP
            ) {
                cabinetGroup.position.z +=
                    prevRowsMaxBboxZ + DUPLICATE_PLACEMENT_GAP - futureBboxMinZ;
                currentRowZ = cabinetGroup.position.z;
                cabinetGroup.updateMatrixWorld();
            }
            // ────────────────────────────────────────────────────────────────

            hasOverlap = true;
            continue;
        }

        // ── Same-row X-overlap check ─────────────────────────────────────────
        const permanentCabinets = sameTypeCabinets.filter(
            (c) => !c.userData.isTempMesh
        );
        const cabinetsInSameRow = permanentCabinets.filter((c) =>
            occupiesZBand(c, cabinetGroup.position.z, ROW_TOLERANCE)
        );

        let rightmostOverlappingMaxX = -Infinity;
        let hasOverlapInSameRow = false;

        for (const cabinet of cabinetsInSameRow) {
            if (checkOBBIntersection(cabinetGroup, cabinet)) {
                const existingBox = new THREE.Box3().setFromObject(cabinet);
                if (existingBox.max.x > rightmostOverlappingMaxX) {
                    rightmostOverlappingMaxX = existingBox.max.x;
                }
                hasOverlapInSameRow = true;
            }
        }

        if (hasOverlapInSameRow) {
            cabinetGroup.position.x +=
                rightmostOverlappingMaxX - currentNewProductBox.min.x;
            cabinetGroup.updateMatrixWorld();
            hasOverlap = true;
        }
    }

    pushPastOverlappingUpperCabinets(
        cabinetGroup,
        existingCabinets,
        scale,
        roomBounds,
        getEffectiveMaxX,
        ROW_Z_OFFSET,
        ROW_TOLERANCE,
        zPivotOffset,
        futureZDepth,
        sameTypeCabinets
    );
    return cabinetGroup.position.clone();
};
